import { useState } from "react";
import { Link } from "react-router-dom";
import { useSilentCustomers, type SilentCustomer } from "@/lib/hooks";
import { fmtInt, fmtDate } from "@/lib/format";
import { useSort } from "@/lib/useSort";
import { downloadXlsx, stampedName } from "@/lib/exportXlsx";
import DownloadButton from "@/components/DownloadButton";

const WEEK_OPTIONS = [2, 3, 4, 6, 8, 12];

const ACCESSORS = {
  name: (c: SilentCustomer) => c.customer_name.toLowerCase(),
  ytd_qty: (c: SilentCustomer) => c.ytd_qty,
  last_order: (c: SilentCustomer) => c.last_order_date,
  days: (c: SilentCustomer) => c.days_since_last_order,
};

export default function Silent() {
  const [weeks, setWeeks] = useState(3);
  const [productGroupKey, setProductGroupKey] = useState<"catheters" | "all">("catheters");
  const productGroups = productGroupKey === "catheters" ? ["UOT", "IAP", "SmartFoley Adapter"] : [];

  const { data, isLoading, error } = useSilentCustomers(weeks, productGroups);
  const { sorted, toggle, arrow } = useSort<SilentCustomer>(
    data?.customers ?? [],
    ACCESSORS,
    "ytd_qty",
    "desc",
  );

  function exportCsv() {
    downloadXlsx(
      stampedName("silent-customers"),
      ["Customer", "YTD qty", "Last order", "Days silent"],
      sorted.map((c) => [c.customer_name, c.ytd_qty, c.last_order_date, c.days_since_last_order]),
    );
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Silent customers</h1>
          <p className="mt-1 text-sm text-ink-500">
            Customers with year-to-date activity that haven't ordered recently.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-ink-500">
            Silent for
            <select
              value={weeks}
              onChange={(e) => setWeeks(Number(e.target.value))}
              className="rounded-md border border-ink-200 px-2 py-1 text-xs text-ink-900"
            >
              {WEEK_OPTIONS.map((w) => (
                <option key={w} value={w}>
                  {w}+ weeks
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-ink-500">
            Product
            <select
              value={productGroupKey}
              onChange={(e) => setProductGroupKey(e.target.value as "catheters" | "all")}
              className="rounded-md border border-ink-200 px-2 py-1 text-xs text-ink-900"
            >
              <option value="catheters">Catheters</option>
              <option value="all">All products</option>
            </select>
          </label>
          <DownloadButton onClick={exportCsv} disabled={!data?.customers.length} />
        </div>
      </header>

      {error && (
        <div className="card mb-6 p-4 text-sm text-danger">{(error as Error).message}</div>
      )}

      {data && (
        <p className="mb-4 text-xs text-ink-500">
          {data.customers.length} customers with no orders since {data.cutoff}.
        </p>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-10 text-center text-sm text-ink-500">Loading…</div>
          ) : !data?.customers.length ? (
            <div className="p-10 text-center text-sm text-ink-500">
              No silent customers — everyone with YTD activity has ordered recently.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-ink-50 text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <SortTh align="left" onClick={() => toggle("name", "asc")}>Customer{arrow("name")}</SortTh>
                  <SortTh align="right" onClick={() => toggle("ytd_qty")}>YTD qty{arrow("ytd_qty")}</SortTh>
                  <SortTh align="right" onClick={() => toggle("last_order")}>Last order{arrow("last_order")}</SortTh>
                  <SortTh align="right" onClick={() => toggle("days")}>Days silent{arrow("days")}</SortTh>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200">
                {sorted.map((c) => (
                  <tr key={c.customer_id} className="hover:bg-ink-50">
                    <td className="px-5 py-2 font-medium text-ink-900">
                      <Link to={`/customers/${encodeURIComponent(c.customer_id)}`} className="hover:text-accent-600">
                        {c.customer_name}
                      </Link>
                    </td>
                    <td className="px-5 py-2 text-right tabular-nums">{fmtInt(c.ytd_qty)}</td>
                    <td className="px-5 py-2 text-right text-ink-500">{fmtDate(c.last_order_date)}</td>
                    <td className="px-5 py-2 text-right tabular-nums text-danger">
                      {c.days_since_last_order ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function SortTh({
  children,
  onClick,
  align,
}: {
  children: React.ReactNode;
  onClick: () => void;
  align: "left" | "right";
}) {
  return (
    <th className={`px-5 py-2 ${align === "left" ? "text-left" : "text-right"}`}>
      <button
        type="button"
        onClick={onClick}
        className="font-medium uppercase tracking-wide text-ink-500 hover:text-ink-900"
      >
        {children}
      </button>
    </th>
  );
}
